// App.js - Complete Fixed Version with unallocated ETH implementation
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useWeb3 } from './hooks/useWeb3';
import { useFundData } from './hooks/useFundData';
import { useTransactions } from './hooks/useTransactions';
import tokenPriceStore from './services/tokenPriceStore'; // Import tokenPriceStore directly
import UnallocatedEthManager from './components/admin/UnallocatedEthManager';

// Enhanced web3Service imports
import { 
  fetchBoardSeats, 
  fetchYILDXTokenBalance, 
  fetchGovernanceInfo,
  exposeContractsToWindow,
  fetchPendingFees,
  claimFees,
  redeemAdminFundTokens,
  recoverDirectlyTransferredTokens
} from './services/web3Service';

// Initialize token price service on app load (outside of component)
import './services/initTokenPrices';

// Import components
import WalletConnect from './components/WalletConnect';
import ErrorDisplay from './components/ErrorDisplay';
import AccountInfo from './components/AccountInfo';
import BalanceCard from './components/BalanceCard';
import PositionsGrid from './components/PositionsGrid';
import TransactionForm from './components/TransactionForm';
import ActionButtons from './components/ActionButtons';
import AboutFund from './components/AboutFund';
import PriceChart from './components/PriceChart';
import AllocationDonutChart from './components/AllocationDonutChart';
import LastUpdatedIndicator from './components/LastUpdatedIndicator';
import AdminPanel from './components/AdminPanel';
import NetworkIndicator from './components/NetworkIndicator';
import ConfirmationPopup from './components/ConfirmationPopup';
import TransactionResultPopup from './components/TransactionResultPopup';
import TokenRedemptionPanel from './components/TokenRedemptionPanel';
import GovernancePanel from './components/admin/GovernancePanel';
import { ethers } from 'ethers';

function App() {
  // Initialize web3 hook
  const web3 = useWeb3();
  const { 
    provider, signer, contract, account,
    governanceContract, yildxTokenContract,
    isLoading: web3Loading, error: web3Error, 
    connect, setError: setWeb3Error 
  } = web3;
  
  // ETH price from central tokenPriceStore (state to force re-renders when updated)
  const [ethPrice, setEthPrice] = useState(0);
  
  // State for unallocated ETH balance
  const [unallocatedEthBalance, setUnallocatedEthBalance] = useState("0");
  
  // Add governance info state
  const [governanceInfo, setGovernanceInfo] = useState({
    boardSeats: 0,
    yildxBalance: '0',
    pendingFees: '0',
    totalSeats: 10,
    votingPower: 0,
    hasGovernanceAccess: false
  });
  
  // State for fund tokens in governance token
  const [govTokenFundBalance, setGovTokenFundBalance] = useState('0');
  
  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [ownerAddress, setOwnerAddress] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Token balance state for governance access
  const [yildxBalance, setYildxBalance] = useState('0');
  
  // Define the fetchUnallocatedEthBalance function inside the component to have access to contract
  const fetchUnallocatedEthBalance = useCallback(async () => {
    if (!contract) return "0";
    
    try {
      // Try calling the unallocatedEthBalance getter function
      const balance = await contract.unallocatedEthBalance();
      return ethers.utils.formatEther(balance);
    } catch (error) {
      console.error("Error fetching unallocated ETH balance:", error);
      return "0";
    }
  }, [contract]);
  
  // Effect to get ETH price directly from tokenPriceStore for consistency
  useEffect(() => {
    // List of known Ethereum addresses in priority order
    const KNOWN_ETH_ADDRESSES = [
      "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2".toLowerCase(), // WETH on Ethereum
      "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619".toLowerCase(), // Polygon ETH
      "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee".toLowerCase(), // Generic ETH address
      "0x0000000000000000000000000000000000000000".toLowerCase(), // Sometimes used for native ETH
      "0x4200000000000000000000000000000000000006".toLowerCase()  // WETH on Optimism
    ];

    // Function to get ETH price from tokenPriceStore
    const getEthPriceFromStore = () => {
      // Try each ETH address in priority order
      for (const ethAddr of KNOWN_ETH_ADDRESSES) {
        const price = tokenPriceStore.getPrice(ethAddr);
        if (price && price > 0) {
          console.log(`App: Found ETH price from tokenPriceStore for ${ethAddr}: ${price}`);
          setEthPrice(price);
          return;
        }
      }
      
      // If we couldn't find a price, check for tokens in the ETH price range
      const priceData = tokenPriceStore.getPrices();
      if (priceData && Object.keys(priceData).length > 0) {
        const ethPriceRange = Object.entries(priceData).filter(([, price]) => 
          price >= 1000 && price <= 5000
        );
        
        if (ethPriceRange.length > 0) {
          console.log(`App: Found potential ETH by price range: ${ethPriceRange[0][0]} - $${ethPriceRange[0][1]}`);
          setEthPrice(ethPriceRange[0][1]);
          return;
        }
      }
      
      // If we still don't have a price, use a default
      if (ethPrice <= 0) {
        console.log("App: Using default ETH price of $3200");
        setEthPrice(3200);
      }
    };

    // Get price initially
    getEthPriceFromStore();

    // Set up listener for price updates
    const handlePriceUpdate = () => {
      getEthPriceFromStore();
    };

    // Add listener to tokenPriceStore
    tokenPriceStore.addListener(handlePriceUpdate);

    // Clean up listener when component unmounts
    return () => {
      tokenPriceStore.removeListener(handlePriceUpdate);
    };
  }, [ethPrice]);
  
  // Use effect to fetch unallocated ETH balance when contract changes
  useEffect(() => {
    const getUnallocatedEth = async () => {
      if (contract) {
        try {
          const unallocatedEth = await fetchUnallocatedEthBalance();
          setUnallocatedEthBalance(unallocatedEth);
          console.log("Unallocated ETH fetched:", unallocatedEth);
        } catch (err) {
          console.error("Error in unallocated ETH effect:", err);
        }
      }
    };
    
    getUnallocatedEth();
  }, [contract, fetchUnallocatedEthBalance]); // Contract dependency
  
  // Memoize web3 object to prevent unnecessary re-renders in child components
  const web3Memo = useMemo(() => {
    return {
      provider,
      signer,
      contract,
      governanceContract,
      yildxTokenContract,
      account,
      isLoading: web3Loading,
      error: web3Error,
      connect,
      setError: setWeb3Error
    };
  }, [provider, signer, contract, governanceContract, yildxTokenContract, account, web3Loading, web3Error, connect]);

  // Pass memoized web3 to useFundData
  const fundData = useFundData(web3Memo);

  // Only log when account or contract changes to reduce console spam
  useEffect(() => {
    if (account || contract) {
      console.log("Fund Data updated due to account/contract change:", fundData);
    }
  }, [account, contract, fundData.nav, fundData.totalSupply]);

  // Destructure fundData with defaults
  const {
    nav = '0',
    contractNav = '0',
    calculatedNav = '0',
    walletEthBalance = '0',
    fundEthBalance = '0',
    fundTokenBalance = '0',
    walletTokenBalance = '0',
    tokenPrice = '0',
    totalSupply = '0',
    positions = [],
    tokenPrices = {},
    isLoading: dataLoading = false,
    error: dataError = null,
    lastUpdated = null,
    fetchAllData: originalFetchAllData,
    setError: setDataError
  } = fundData;

  // Wrap fetchAllData to also fetch unallocated ETH
  const enhancedFetchAllData = useCallback(async () => {
    // Call the original fetchAllData
    const result = await originalFetchAllData();
    
    // Also fetch unallocated ETH
    if (contract) {
      try {
        const unallocatedEth = await fetchUnallocatedEthBalance();
        setUnallocatedEthBalance(unallocatedEth);
        console.log("Unallocated ETH refreshed:", unallocatedEth);
      } catch (err) {
        console.error("Error refreshing unallocated ETH:", err);
      }
    }
    
    return result;
  }, [originalFetchAllData, contract, fetchUnallocatedEthBalance]);

  // Memoize fundData for transactions to prevent unnecessary re-renders
  const fundDataMemo = useMemo(() => {
    return {
      nav, contractNav, calculatedNav, walletEthBalance, fundEthBalance,
      fundTokenBalance, walletTokenBalance, tokenPrice, totalSupply,
      positions, tokenPrices, isLoading: dataLoading,
      error: dataError, lastUpdated, 
      fetchAllData: enhancedFetchAllData, // Use enhanced version
      setError: setDataError,
      ethPrice, // Add centralized ethPrice to fundDataMemo
      unallocatedEthBalance // Add unallocated ETH balance
    };
  }, [nav, contractNav, tokenPrice, totalSupply, walletEthBalance,
      fundEthBalance, fundTokenBalance, walletTokenBalance, positions, 
      dataLoading, dataError, lastUpdated, ethPrice, unallocatedEthBalance,
      enhancedFetchAllData]);
  
  console.log('LOGS', positions, tokenPrices, 'ETH Price:', ethPrice, 'Unallocated ETH:', unallocatedEthBalance);
  
  // Pass memoized objects to useTransactions
  const transactions = useTransactions(web3Memo, fundDataMemo);

  // Destructure transactions with defaults
  const {
    inputAmount = '',
    setInputAmount,
    isLoading: txLoading = false,
    depositETH, withdrawETH, mintShares, withdrawTokens,
    redeemFundTokens, redeemWalletTokens, depositTokens,
    redeemTokenByIndex
  } = transactions;

  // UI mode state
  const [mode, setMode] = useState('deposit');
  
  // Token redemption panel state
  const [showTokenRedemption, setShowTokenRedemption] = useState(false);

  // Confirmation popup state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    title: '',
    message: '',
    actionType: '',
    amount: '',
    onConfirm: () => {}
  });

  // Transaction result popup state
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState({
    status: 'pending',
    actionType: '',
    amount: '',
    message: '',
    txHash: '',
    startTime: 0
  });

  // CRITICAL FIX: Updated toggleMode function with logging
  const toggleMode = useCallback(() => {
    console.log("App: Toggle mode from", mode, "to", mode === 'mint' ? 'deposit' : 'mint');
    setMode(prev => (prev === 'mint' ? 'deposit' : 'mint'));
    // Clear input amount when switching modes
    setInputAmount('');
    // Hide token redemption panel when switching modes
    setShowTokenRedemption(false);
  }, [mode, setInputAmount]);
  
  // Toggle token redemption panel visibility
  const toggleTokenRedemption = useCallback(() => {
    setShowTokenRedemption(prev => !prev);
  }, []);
  
  // Combined loading and error states
  const isLoading = web3Loading || dataLoading || txLoading;
  const error = web3Error || dataError;

  // Memoize clearError function
  const clearError = useCallback(() => {
    setWeb3Error("");
    setDataError("");
  }, [setWeb3Error, setDataError]);

  // Enhanced admin, operator, and governance check
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!contract || !account) {
        if (!contract) console.log("App: Contract instance not available");
        if (!account) console.log("App: Account not connected");
        setIsAdmin(false);
        setIsOperator(false);
        setYildxBalance('0');
        return;
      }

      try {
        // First check if account is the contract owner (traditional admin)
        console.log("App: Checking admin status for account:", account);
        
        // Debug contract functions
        try {
          console.log("App: Contract functions available:", 
            Object.keys(contract.functions).join(", "));
          console.log("App: Has authorizedOperators function:", 
            Object.keys(contract.functions).includes("authorizedOperators"));
          console.log("App: Contract ABI fragment:", JSON.stringify(contract.interface.fragments[0]));
        } catch (e) {
          console.error("App: Error logging contract functions:", e.message);
        }
        
        const owner = await contract.owner();
        setOwnerAddress(owner);
        const isOwner = owner.toLowerCase() === account.toLowerCase();
        console.log("App: Contract owner is:", owner);
        console.log("App: Is account the contract owner?", isOwner);
        
        // Check if user is an authorized operator - IMPROVED DETECTION
        let isUserOperator = false;
        try {
          console.log("App: Checking if account is an operator using authorizedOperators...");
          // First check if the authorizedOperators method exists
          isUserOperator = await contract.authorizedOperators(account);
          console.log("App: authorizedOperators check for", account, "result:", isUserOperator);
        } catch (operatorError) {
          console.error("App: Error checking operator status with authorizedOperators:", operatorError.message);
          
          // Try an alternative way to check operator status
          try {
            console.log("App: Trying alternative isOperator check...");
            const isOperator = await contract.isOperator(account);
            isUserOperator = isOperator;
            console.log("App: isOperator alternative check result:", isUserOperator);
          } catch (altError) {
            console.error("App: Alternative operator check also failed:", altError.message);
            
            // Try a third method
            try {
              console.log("App: Trying third method - checking operators mapping...");
              const isOpCheck = await contract.operators(account);
              isUserOperator = isOpCheck;
              console.log("App: operators mapping check result:", isUserOperator);
            } catch (thirdError) {
              console.error("App: Third operator check method failed:", thirdError.message);
            }
          }
        }
        
        // Make one final check with a raw call if all else fails
        if (!isUserOperator) {
          try {
            console.log("App: Making raw call to check operator status...");
            // Create a function selector for authorizedOperators(address)
            const selector = ethers.utils.id("authorizedOperators(address)").slice(0, 10);
            const encodedAddress = ethers.utils.defaultAbiCoder.encode(["address"], [account]).slice(2);
            const data = selector + encodedAddress;
            
            const result = await provider.call({
              to: contract.address,
              data: data
            });
            
            console.log("App: Raw call result:", result);
            // Decode boolean result
            isUserOperator = result === '0x0000000000000000000000000000000000000000000000000000000000000001';
            console.log("App: Operator status from raw call:", isUserOperator);
          } catch (rawError) {
            console.error("App: Raw call to check operator status failed:", rawError.message);
          }
        }
        
        // Set states based on checks
        console.log("App: Setting admin state to:", isOwner);
        console.log("App: Setting operator state to:", isUserOperator);
        setIsAdmin(isOwner);
        setIsOperator(isUserOperator);
        
        // Add this to see if the state was updated properly
        setTimeout(() => {
          console.log("App: After state update - isAdmin:", isAdmin, "isOperator:", isOperator);
        }, 100);
        
        // Then check YILDX token balance for governance participation
        if (yildxTokenContract) {
          try {
            const balance = await yildxTokenContract.balanceOf(account);
            const formattedBalance = ethers.utils.formatEther(balance);
            setYildxBalance(formattedBalance);
            console.log(`App: User has ${formattedBalance} YILDX tokens`);
            
            // Get governance info if we have access to the governance contract
            if (governanceContract) {
              const govInfo = await fetchGovernanceInfo(yildxTokenContract, governanceContract, account);
              setGovernanceInfo(govInfo);
            }
            
            // NEW: Check for fund tokens in the governance token contract
            if (contract && yildxTokenContract) {
              try {
                const fundTokens = await contract.balanceOf(yildxTokenContract.address);
                const formattedFundTokens = ethers.utils.formatEther(fundTokens);
                setGovTokenFundBalance(formattedFundTokens);
                console.log(`App: Found ${formattedFundTokens} fund tokens in governance token contract`);
              } catch (ftError) {
                console.error("App: Error checking fund tokens in governance contract:", ftError.message);
              }
            }
          } catch (tokenError) {
            console.error("App: Error checking YILDX token balance:", tokenError.message);
            setYildxBalance('0');
          }
        }
        
        // Expose contracts to window for debugging (only in development)
        if (process.env.NODE_ENV === 'development') {
          exposeContractsToWindow({
            provider, signer, account, 
            fundContract: contract, 
            governanceContract, 
            yildxTokenContract
          });
        }
      } catch (error) {
        console.error("App: Error checking admin status:", error.message);
        setIsAdmin(false);
        setIsOperator(false);
        setYildxBalance('0');
      }
    };

    // Debounce the admin check to avoid multiple rapid calls
    const timer = setTimeout(() => {
      checkAdminStatus();
    }, 300);

    return () => clearTimeout(timer);
  }, [contract, account, yildxTokenContract, governanceContract, provider, signer, isAdmin, isOperator]);

  // Check transaction status - improved with fallback timeout
  useEffect(() => {
    let interval;
    let maxTimeoutId;

    if (showResult && resultData.status === 'pending' && resultData.txHash && provider) {
      // Set a maximum time to wait for transaction confirmation (2 minutes)
      // After this time, we'll automatically refresh data and let the user continue
      const MAX_WAIT_TIME = 2 * 60 * 1000; // 2 minutes
      
      if (resultData.startTime && (Date.now() - resultData.startTime) > MAX_WAIT_TIME) {
        console.log("Transaction monitoring timeout reached. Refreshing data and showing success.");
        // Force refresh data
        enhancedFetchAllData();
        // Assume success after maximum wait time (user can see actual state in UI after refresh)
        setResultData(prev => ({ 
          ...prev, 
          status: 'success',
          message: `Your ${prev.actionType} transaction likely succeeded. We've refreshed your data.`
        }));
        return;
      }

      // Set the max timeout only once
      if (!maxTimeoutId && resultData.startTime) {
        maxTimeoutId = setTimeout(() => {
          if (showResult && resultData.status === 'pending') {
            console.log("Transaction monitoring timeout reached. Refreshing data and showing success.");
            // Force refresh data
            enhancedFetchAllData();
            // Assume success after maximum wait time
            setResultData(prev => ({ 
              ...prev, 
              status: 'success',
              message: `Your ${prev.actionType} transaction likely succeeded. We've refreshed your data.`
            }));
          }
        }, MAX_WAIT_TIME - (Date.now() - resultData.startTime));
      }

      interval = setInterval(async () => {
        try {
          // First check balance changes for deposits/withdrawals
          // This is a more reliable way to detect successful transactions in many cases
          if (
            resultData.actionType === 'deposit' || 
            resultData.actionType === 'withdraw' ||
            resultData.actionType === 'mint' ||
            resultData.actionType === 'redeem fund tokens' ||
            resultData.actionType === 'redeem wallet tokens' ||
            resultData.actionType === 'redeem token' // Add new action type
          ) {
            // Refresh data to see if balances changed
            await enhancedFetchAllData();
          }
          
          // Then check transaction receipt
          const receipt = await provider.getTransactionReceipt(resultData.txHash);
          if (receipt) {
            // Transaction is mined
            if (receipt.status === 1) {
              // Success
              setResultData(prev => ({ ...prev, status: 'success' }));
              // Refresh data again to ensure we have the latest
              enhancedFetchAllData();
            } else {
              // Failed
              setResultData(prev => ({ ...prev, status: 'failed' }));
            }
            clearInterval(interval);
            if (maxTimeoutId) clearTimeout(maxTimeoutId);
          }
        } catch (error) {
          console.error("Error checking transaction status:", error);
          
          // If we've been trying for a while and still getting errors, refresh data anyway
          if (resultData.startTime && (Date.now() - resultData.startTime) > 45000) { // 45 seconds
            enhancedFetchAllData();
          }
        }
      }, 3000); // Check every 3 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
      if (maxTimeoutId) clearTimeout(maxTimeoutId);
    };
  }, [showResult, resultData.status, resultData.txHash, resultData.actionType, resultData.startTime, provider, enhancedFetchAllData]);

  // Transaction wrapper function with pending and result handling
  const handleTransaction = useCallback(async (action, actionType, amount) => {
    try {
      // Start timestamp for transaction timing
      const startTime = Date.now();
      
      // Check if this is a mint or redeem operation
      const isMintOperation = actionType.toLowerCase().includes('mint');
      const isRedeemOperation = actionType.toLowerCase().includes('redeem');
      
      // Create appropriate pending message
      let pendingMessage = `Your ${actionType} transaction is waiting for your confirmation in MetaMask.`;
      
      if (isMintOperation) {
        pendingMessage = `Your mint transaction is being prepared. This will purchase tokens according to the fund's current allocations.`;
      } else if (isRedeemOperation) {
        pendingMessage = `Your redeem transaction is being prepared. This will convert your fund tokens to ETH based on current token values.`;
      }
      
      // First show the "waiting for confirmation" screen
      setResultData({
        status: 'pending',
        actionType,
        amount,
        message: pendingMessage,
        txHash: '',
        startTime
      });
      setShowResult(true);
      
      console.log("App: About to execute transaction action...");
      console.log("App: Provider available:", !!provider);
      
      // Execute the transaction
      const tx = await action();
      console.log("App: Transaction submitted:", tx);
      
      // Ensure we have a transaction hash
      let txHash = '';
      
      // Check if tx is a transaction object with hash
      if (tx && typeof tx === 'object') {
        if (tx.hash) {
          txHash = tx.hash;
        } else if (tx.transactionHash) {
          // Some libraries return transactionHash instead of hash
          txHash = tx.transactionHash;
        }
      }
      
      // Log transaction details for debugging
      console.log("App: Transaction object type:", typeof tx);
      console.log("App: Transaction object keys:", tx ? Object.keys(tx) : "No transaction object");
      console.log("App: Transaction hash extracted:", txHash);
      
      if (!txHash) {
        console.warn("App: Transaction executed but no hash was returned. Will still continue with data refresh.");
      }
      
      // Create appropriate submitted message
      let submittedMessage = `Your ${actionType} transaction has been submitted to the blockchain and is waiting to be mined.`;
      
      if (isMintOperation) {
        submittedMessage = `Your mint transaction has been submitted to the blockchain. Once confirmed, your ETH will be converted to tokens.`;
      } else if (isRedeemOperation) {
        submittedMessage = `Your redeem transaction has been submitted to the blockchain. Once confirmed, your tokens will be converted to ETH.`;
      }
      
      // Update popup to show "pending on blockchain" with tx hash
      setResultData(prev => ({
        ...prev,
        message: submittedMessage,
        txHash: txHash
      }));
      
      console.log("App: Updated result data with hash:", txHash);
      console.log("App: Provider being passed to popup:", provider ? "Available" : "Not available");
      
      // Immediately refresh data to get the latest state
      await enhancedFetchAllData();
      
      // Clear input amount
      setInputAmount('');
      
      // Return the transaction
      return tx;
      
    } catch (error) {
      console.error("App: Transaction failed:", error);
      
      // IMPROVED ERROR HANDLING: Check for specific error messages
      const errorMessage = error.message || '';
      
      // Handle user rejections specially
      if (error.code === 4001 || 
          error.code === "ACTION_REJECTED" || 
          error.isCancelled || 
          errorMessage.includes("user rejected transaction")) {
        
        console.log("App: Transaction was cancelled by user");
        
        // Show cancelled message
        setResultData({
          status: 'cancelled',
          actionType,
          amount,
          message: `Your ${actionType} transaction was cancelled.`,
          txHash: '',
          startTime: 0
        });
        setShowResult(true);
        
        return null; // Return null for cancelled transactions
      }
      
      // Handle insufficient fund token balance errors
      if (errorMessage.includes("Insufficient fund token balance") ||
          errorMessage.includes("insufficient funds") ||
          errorMessage.includes("exceeds balance") ||
          errorMessage.includes("UNPREDICTABLE_GAS_LIMIT")) {
        
        // Extract the specific error message if possible
        let specificMessage = "Insufficient balance for this transaction.";
        
        // Try to extract the actual error message from the error data
        if (error.data) {
          try {
            // This is a common format for Ethereum errors
            const hexData = error.data.substring(10); // Remove the "0x..." prefix and selector
            const abiCoder = new ethers.utils.AbiCoder();
            const decoded = abiCoder.decode(["string"], "0x" + hexData);
            if (decoded && decoded[0]) {
              specificMessage = decoded[0];
            }
          } catch (decodeError) {
            console.error("App: Failed to decode error data:", decodeError);
          }
        }
        
        // For deep nested errors in UNPREDICTABLE_GAS_LIMIT
        if (error.error && error.error.message) {
          specificMessage = error.error.message;
        }
        
        // Show friendly error message
        setResultData({
          status: 'failed',
          actionType,
          amount,
          message: specificMessage,
          txHash: '',
          startTime: 0
        });
        setShowResult(true);
        
        return null;
      }
      
      // Handle other errors
      setResultData({
        status: 'failed',
        actionType,
        amount,
        message: error.message || `Your ${actionType} transaction failed.`,
        txHash: '',
        startTime: 0
      });
      setShowResult(true);
      
      throw error; // Re-throw to allow caller to handle if needed
    }
  }, [setInputAmount, enhancedFetchAllData, provider]);

  // Now we can define handleDepositTokens AFTER handleTransaction
  const handleDepositTokens = useCallback(() => {
    const amount = inputAmount || walletTokenBalance;
    
    setConfirmationData({
      title: 'Confirm Fund Token Deposit',
      message: 'Are you sure you want to deposit fund tokens back to the fund?',
      actionType: 'deposit tokens',
      amount,
      onConfirm: () => handleTransaction(() => depositTokens(), 'deposit tokens', amount)
    });
    setShowConfirmation(true);
  }, [inputAmount, walletTokenBalance, depositTokens, handleTransaction]);

  // Manual refresh after transaction
  const handleManualRefresh = useCallback(async () => {
    console.log("App: Manual refresh triggered");
    
    // Wait for data refresh to complete
    await enhancedFetchAllData();
    
    // For mint/redeem operations, show success message with details
    if (resultData.status === 'pending') {
      const isMintOperation = resultData.actionType.toLowerCase().includes('mint');
      const isRedeemOperation = resultData.actionType.toLowerCase().includes('redeem');
      
      let successMessage = `Your ${resultData.actionType} transaction was successful.`;
      
      if (isMintOperation) {
        successMessage = `Your mint transaction was successful. You've received tokens according to the fund's allocations.`;
      } else if (isRedeemOperation) {
        successMessage = `Your redeem transaction was successful. You've received ETH in exchange for your tokens.`;
      }
      
      setResultData(prev => ({ 
        ...prev, 
        status: 'success',
        message: successMessage
      }));
    }
  }, [enhancedFetchAllData, resultData.status, resultData.actionType]);

  // Wrapper functions for transaction actions with confirmation
  const handleDeposit = useCallback(() => {
    const amount = inputAmount;
    
    setConfirmationData({
      title: 'Confirm Deposit',
      message: 'Are you sure you want to deposit ETH into the fund?',
      actionType: 'deposit',
      amount,
      onConfirm: () => handleTransaction(() => depositETH(), 'deposit', amount)
    });
    setShowConfirmation(true);
  }, [inputAmount, depositETH, handleTransaction]);

  const handleWithdraw = useCallback(() => {
    const amount = inputAmount;
    
    setConfirmationData({
      title: 'Confirm Withdrawal',
      message: 'Are you sure you want to withdraw ETH from the fund?',
      actionType: 'withdraw',
      amount,
      onConfirm: () => handleTransaction(() => withdrawETH(), 'withdraw', amount)
    });
    setShowConfirmation(true);
  }, [inputAmount, withdrawETH, handleTransaction]);

const handleRecoverTokens = useCallback(() => {
  setConfirmationData({
    title: 'Recover Directly Transferred Tokens',
    message: `This will attempt to recover fund tokens that were directly transferred to the contract without proper tracking.

IMPORTANT DETAILS:
- This ONLY recovers tokens that are currently in your wallet
- We cannot recover tokens that were sent directly to the contract by someone else
- In the future, always use the "Deposit MCIF to Fund" button instead of direct transfers

This recovery process works by making your tokens visible to the fund's tracking system, allowing you to use them within the fund.`,
    actionType: 'recover tokens',
    amount: '',
    onConfirm: () => handleTransaction(
      () => recoverDirectlyTransferredTokens(contract, signer), 
      'recover tokens', 
      ''
    )
  });
  setShowConfirmation(true);
}, [contract, signer, handleTransaction]);

  // CRITICAL FIX: Updated handleMint with better logging
  const handleMint = useCallback(() => {
    // For minting, use ETH balance
    const amount = inputAmount || fundEthBalance;
    
    console.log("App: handleMint using amount:", amount, "from inputAmount:", inputAmount, "or fundEthBalance:", fundEthBalance);
    
    setConfirmationData({
      title: 'Confirm Minting',
      message: 'Are you sure you want to mint fund tokens with your ETH deposit?',
      actionType: 'mint',
      amount,
      onConfirm: () => handleTransaction(() => mintShares(), 'mint', amount)
    });
    setShowConfirmation(true);
  }, [inputAmount, fundEthBalance, mintShares, handleTransaction]);

  const handleWithdrawTokens = useCallback(() => {
    const amount = inputAmount || fundTokenBalance;
    
    setConfirmationData({
      title: 'Confirm Token Withdrawal',
      message: 'Are you sure you want to withdraw fund tokens to your wallet?',
      actionType: 'withdraw',
      amount,
      onConfirm: () => handleTransaction(() => withdrawTokens(), 'withdraw tokens', amount)
    });
    setShowConfirmation(true);
  }, [inputAmount, fundTokenBalance, withdrawTokens, handleTransaction]);

  // CRITICAL FIX: Updated handleRedeemFund with validation and better logging
  const handleRedeemFund = useCallback(() => {
    // For redemption, use fund token balance
    const amount = inputAmount || fundTokenBalance;
    
    console.log("App: handleRedeemFund using amount:", amount, "from inputAmount:", inputAmount, "or fundTokenBalance:", fundTokenBalance);
    
    // Validate amount doesn't exceed fundTokenBalance
    let safeAmount = amount;
    
    try {
      // Validate that we don't exceed the fund token balance
      const amountBN = ethers.utils.parseEther(amount);
      const balanceBN = ethers.utils.parseEther(fundTokenBalance);
      
      if (amountBN.gt(balanceBN)) {
        // If amount exceeds balance, use 99.5% of max
        safeAmount = ethers.utils.formatEther(balanceBN.mul(995).div(1000));
        console.log(`App: Redemption amount ${amount} exceeds balance ${fundTokenBalance}, reducing to ${safeAmount}`);
      }
    } catch (error) {
      console.error("App: Error validating fund redemption amount:", error);
    }
    
    setConfirmationData({
      title: 'Confirm Fund Token Redemption',
      message: 'Are you sure you want to redeem your fund tokens for ETH?',
      actionType: 'redeem',
      amount: safeAmount,
      onConfirm: () => handleTransaction(() => redeemFundTokens(), 'redeem fund tokens', safeAmount)
    });
    setShowConfirmation(true);
  }, [inputAmount, fundTokenBalance, redeemFundTokens, handleTransaction]);

  const handleRedeemWallet = useCallback(() => {
    const amount = inputAmount || walletTokenBalance;
    
    setConfirmationData({
      title: 'Confirm Wallet Token Redemption',
      message: 'Are you sure you want to redeem your wallet tokens for ETH?',
      actionType: 'redeem',
      amount,
      onConfirm: () => handleTransaction(() => redeemWalletTokens(), 'redeem wallet tokens', amount)
    });
    setShowConfirmation(true);
  }, [inputAmount, walletTokenBalance, redeemWalletTokens, handleTransaction]);

  // CRITICAL FIX: Updated handleTokenRedemption with validation
  const handleTokenRedemption = useCallback((tokenIndex, amount, tokenSymbol) => {
    // Verify the amount against fundTokenBalance to prevent errors
    let safeAmount = amount;
    
    try {
      // Validate that we don't exceed the fund token balance
      const amountBN = ethers.utils.parseEther(amount);
      const balanceBN = ethers.utils.parseEther(fundTokenBalance);
      
      if (amountBN.gt(balanceBN)) {
        // If amount exceeds balance, use 99.5% of max
        safeAmount = ethers.utils.formatEther(balanceBN.mul(995).div(1000));
        console.log(`App: Token redemption amount ${amount} exceeds balance ${fundTokenBalance}, reducing to ${safeAmount}`);
      }
    } catch (error) {
      console.error("App: Error validating token redemption amount:", error);
    }
    
    setConfirmationData({
      title: 'Confirm Token Redemption',
      message: `Are you sure you want to redeem your fund tokens for ${tokenSymbol}?`,
      actionType: 'redeem token',
      amount: safeAmount,
      onConfirm: () => handleTransaction(
        () => redeemTokenByIndex(tokenIndex, safeAmount), 
        `redeem token (${tokenSymbol})`, 
        safeAmount
      )
    });
    setShowConfirmation(true);
  }, [redeemTokenByIndex, handleTransaction, fundTokenBalance]);

  // Function to handle claiming governance fees - UPDATED to support the new redemption method
  const handleClaimFees = useCallback(async () => {
    if (!yildxTokenContract || !contract) {
      console.error("YILDX Token contract or Fund contract not available");
      return;
    }
    
    try {
      // First check if there are any fees to claim before sending transaction
      console.log("App: Checking fees before claiming...");
      
      // Check fund tokens in governance token if available
      let hasFundTokens = false;
      let fundTokenAmount = '0';
      if (contract) {
        try {
          const govTokenFundBalance = await contract.balanceOf(yildxTokenContract.address);
          hasFundTokens = govTokenFundBalance.gt(0);
          fundTokenAmount = ethers.utils.formatEther(govTokenFundBalance);
          console.log(`App: Fund tokens in governance token: ${fundTokenAmount} (${hasFundTokens ? 'available' : 'none'})`);
        } catch (err) {
          console.error("App: Error checking fund tokens in governance token:", err);
        }
      }
      
      // Check direct ETH fees
      let hasPendingFees = false;
      let pendingFeesAmount = '0';
      try {
        const pendingFees = await yildxTokenContract.pendingFees(account);
        hasPendingFees = pendingFees.gt(0);
        pendingFeesAmount = ethers.utils.formatEther(pendingFees);
        console.log(`App: Pending ETH fees: ${pendingFeesAmount} (${hasPendingFees ? 'available' : 'none'})`);
      } catch (err) {
        console.error("App: Error checking pending fees:", err);
      }
      
      // If no fees of either type, show an error as a popup
      if (!hasFundTokens && !hasPendingFees) {
        console.log("App: No fees available to claim");
        
        // Show friendly message in result popup
        setResultData({
          status: 'failed',
          actionType: 'claim fees',
          amount: '0',
          message: 'There are no fees available to claim at this time.',
          txHash: '',
          startTime: 0
        });
        setShowResult(true);
        
        return null;
      }
      
      // Start fee redemption process
      setResultData({
        status: 'pending',
        actionType: 'claim fees',
        amount: hasFundTokens ? fundTokenAmount : pendingFeesAmount,
        message: 'Your fee claim transaction is being prepared.',
        txHash: '',
        startTime: Date.now()
      });
      setShowResult(true);
      
      // Check if we should use the new redemption method or the old claim method
      if (hasFundTokens) {
        console.log("App: Found fund tokens in governance token, using redemption method");
        console.log("App: Fund token balance:", fundTokenAmount);
        
        // Get fund tokens in governance contract
        const govTokenFundBalance = await contract.balanceOf(yildxTokenContract.address);
        
        // Use the new redemption method
        const tx = await contract.redeemAdminFundTokens(govTokenFundBalance);
        console.log("App: Fee redemption transaction submitted:", tx.hash);
        
        // Update popup with hash
        setResultData(prev => ({
          ...prev,
          txHash: tx.hash,
          message: 'Your fee redemption transaction has been submitted to the blockchain.'
        }));
        
        // Wait for transaction
        await tx.wait();
        
        // Update popup status
        setResultData(prev => ({
          ...prev,
          status: 'success',
          message: 'Successfully redeemed management fees from governance token'
        }));
        
        // Refresh data
        await enhancedFetchAllData();
        
        return tx;
      } else if (hasPendingFees) {
        console.log("App: Using direct claim method for ETH fees");
        console.log("App: Pending fees:", pendingFeesAmount, "ETH");
        
        // Use the original claim method
        const tx = await yildxTokenContract.claimFees();
        console.log("App: Fee claim transaction submitted:", tx.hash);
        
        // Update popup with hash
        setResultData(prev => ({
          ...prev,
          txHash: tx.hash,
          message: 'Your fee claim transaction has been submitted to the blockchain.'
        }));
        
        // Wait for transaction
        await tx.wait();
        
        // Update popup status
        setResultData(prev => ({
          ...prev,
          status: 'success',
          message: 'Successfully claimed governance fees'
        }));
        
        // Refresh data
        await enhancedFetchAllData();
        
        return tx;
      } else {
        // This shouldn't happen due to our earlier check
        console.log("App: No fees to claim after double-check");
        
        // Update popup status
        setResultData(prev => ({
          ...prev,
          status: 'failed',
          message: 'No fees available to claim'
        }));
        
        return null;
      }
    } catch (error) {
      console.error("App: Error claiming/redeeming fees:", error);
      
      // Provide a more user-friendly error message
      let errorMessage = error.message || 'An error occurred while claiming fees';
      
      // Check for specific error messages and provide clearer responses
      if (error.message && error.message.includes('No fees to claim')) {
        errorMessage = 'There are no fees available to claim at this time.';
      } else if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setResultData(prev => ({
          ...prev,
          status: 'cancelled',
          message: 'You cancelled the transaction.'
        }));
        return null;
      }
      
      // Update popup status with the error
      setResultData(prev => ({
        ...prev,
        status: 'failed',
        message: errorMessage
      }));
      
      throw error;
    }
  }, [yildxTokenContract, contract, account, enhancedFetchAllData]);

  // Determine if we should show governance panel based on token balance
  const hasGovernanceAccess = parseFloat(yildxBalance) > 0;

  // CRITICAL LOGGING: Log state changes for debugging
  useEffect(() => {
    console.log("App: Mode changed to:", mode);
  }, [mode]);
  
  useEffect(() => {
    console.log("App: Token balances updated - Fund:", fundTokenBalance, "Wallet:", walletTokenBalance);
  }, [fundTokenBalance, walletTokenBalance]);
  
  useEffect(() => {
    console.log("App: Input amount updated:", inputAmount);
  }, [inputAmount]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center">
      {/* Loading indicator */}
      {isLoading && (
        <div className="fixed top-0 left-0 right-0 bg-blue-500 text-white p-3 text-center z-50">
          Processing transaction... Please wait
        </div>
      )}
      
      {/* Confirmation Popup */}
      <ConfirmationPopup
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => {
          const { onConfirm } = confirmationData;
          setShowConfirmation(false);
          onConfirm();
        }}
        title={confirmationData.title}
        message={confirmationData.message}
        actionType={confirmationData.actionType}
        amount={confirmationData.amount}
        loading={isLoading}
      />
      
      {/* Transaction Result Popup */}
      <TransactionResultPopup
        isOpen={showResult}
        onClose={() => setShowResult(false)}
        onRefresh={handleManualRefresh}
        status={resultData.status}
        actionType={resultData.actionType}
        amount={resultData.amount}
        message={resultData.message}
        txHash={resultData.txHash}
        provider={provider}
        // Pass position and price data for enhanced mint/redeem display
        positions={positions}
        tokenPrices={tokenPrices}
        fundData={fundDataMemo}
      />
      
      {/* Main content container */}
      <div className="w-full bg-white">
        {!account ? (
          <div className="mt-10 flex justify-center">
            <WalletConnect isLoading={isLoading} onConnect={connect} />
          </div>
        ) : (
          <>
            {/* Network indicator and admin panel - only show when connected */}
            <div className="space-y-8 p-8 max-w-[min(80rem,calc(100svw-40px))] mx-auto mb-2">
              <NetworkIndicator provider={provider} />
              
              {/* Admin/Governance button - show based on role */}
              <div className="flex items-center space-x-4">
                {(isAdmin || isOperator || hasGovernanceAccess) && (
                  <button
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition"
                    onClick={() => setShowAdminPanel(!showAdminPanel)}
                  >
                    {showAdminPanel ? "Hide Dashboard" : (isAdmin || isOperator) ? "Admin Dashboard" : "Governance Dashboard"}
                  </button>
                )}
                
                {/* Show role badge */}
                {(isAdmin || isOperator || hasGovernanceAccess) && (
                  <div className="flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg px-3 py-2 shadow-sm">
                    {isAdmin && (
                      <span className="text-sm font-medium bg-blue-600 text-white rounded-full px-3 py-1">
                        Admin
                      </span>
                    )}
                    {!isAdmin && isOperator && (
                      <span className="text-sm font-medium bg-green-600 text-white rounded-full px-3 py-1">
                        Operator
                      </span>
                    )}
                    {hasGovernanceAccess && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-indigo-700">YILDX Balance:</span>
                        <span className="text-sm font-bold bg-indigo-600 text-white rounded-full px-3 py-1">
                          {parseFloat(yildxBalance).toLocaleString(undefined, {maximumFractionDigits: 2})}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Error display */}
            {error && (
              <div className="space-y-8 p-8 max-w-[min(80rem,calc(100svw-40px))] mx-auto mb-2">
                <ErrorDisplay error={error} onClear={clearError} />
              </div>
            )}

            {/* Admin/Governance Panel - Show based on access level */}
            {showAdminPanel && (
              <div className="space-y-8 p-8 max-w-[min(80rem,calc(100svw-40px))] mx-auto mb-2">
                <AdminPanel 
                  web3={web3Memo} 
                  isOwner={isAdmin} 
                  isOperator={isOperator}
                  governanceInfo={{
                    ...governanceInfo,
                    govTokenFundBalance // Add fund token balance to governance info
                  }}
                  onClaimFees={handleClaimFees}
                />
              </div>
            )}

            {/* Main app content */}
            <div className="space-y-8 p-8 max-w-[min(80rem,calc(100svw-40px))] mx-auto">
              <AccountInfo 
                account={account}
                isLoading={isLoading}
                onRefresh={enhancedFetchAllData}
              />
              
              {/* Price Update Indicator */}
              <div className="flex justify-end">
                <LastUpdatedIndicator 
                  lastUpdated={lastUpdated} 
                  isLoading={dataLoading} 
                />
              </div>
              
              {/* Fund Overview Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 positions">
                {/* Asset Allocation Chart */}
                <AllocationDonutChart 
                  positions={positions || []} 
                  fundTokenBalance={fundTokenBalance}
                  walletTokenBalance={walletTokenBalance}
                  totalSupply={totalSupply}
                  unallocatedEthBalance={unallocatedEthBalance}
                  ethPrice={ethPrice}
                  nav={nav}                  
                  contractNav={contractNav}
                  calculatedNav={calculatedNav}
                  navUsd={fundDataMemo.navUsd}
                />
                <BalanceCard
                  walletEthBalance={walletEthBalance}
                  walletTokenBalance={walletTokenBalance}
                  fundEthBalance={fundEthBalance}
                  fundTokenBalance={fundTokenBalance}
                  nav={nav}
                  contractNav={contractNav}
                  calculatedNav={calculatedNav}
                  tokenPrice={tokenPrice}
                  totalSupply={totalSupply}
                  tokenPrices={tokenPrices || {}}
                  unallocatedEthBalance={unallocatedEthBalance}
                />
              </div>

              {/* Fund Positions Section */}
              <div className="rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-xl font-bold text-gray-700">Fund Positions</h2>
                  <button 
                    className="py-2 px-4 rounded-lg font-medium text-white bg-blue-500 hover:bg-blue-600
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition"
                    onClick={() => {
                      // Find and manually trigger setShowTransactions on the PositionsGrid component
                      const event = new CustomEvent('toggleTransactions');
                      document.dispatchEvent(event);
                    }}
                    style={{ cursor: 'pointer', zIndex: 50 }}
                  >
                    View Transaction History
                  </button>
                </div>
                <PositionsGrid 
                  positions={positions || []} 
                  provider={provider}
                  contract={contract}
                  account={account}
                  ethPrice={ethPrice}
                  unallocatedEthBalance={unallocatedEthBalance}
                  fundTokenBalance={fundTokenBalance}
                  walletTokenBalance={walletTokenBalance}
                  totalSupply={totalSupply}
                  nav={nav}
                  contractNav={contractNav}
                  calculatedNav={calculatedNav}
                  navUsd={fundDataMemo.navUsd}
                />
              </div>
              
              {/* Transactions Section */}
              <div className="rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden">
                <h2 className="text-xl font-bold text-gray-700 mb-4 flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
                  Transaction Management
                  <button
                    onClick={toggleMode}
                    className="py-2 px-4 rounded-lg font-medium text-white bg-blue-500 hover:bg-blue-600
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition"
                    style={{cursor: 'pointer', position: 'relative', zIndex: 50}}
                  >
                    {mode === 'mint' ? 'Switch to Deposit/Withdraw' : 'Switch to Mint/Redeem'}
                  </button>
                </h2>
                
                {/* CRITICAL FIX: Pass all balance props to TransactionForm */}
                <TransactionForm
                  inputAmount={inputAmount}
                  setInputAmount={setInputAmount}
                  isLoading={isLoading}
                  tokenType={transactions.tokenType}
                  setTokenType={transactions.setTokenType}
                  mode={mode}
                  walletEthBalance={walletEthBalance}
                  fundEthBalance={fundEthBalance}
                  walletTokenBalance={walletTokenBalance}
                  fundTokenBalance={fundTokenBalance}
                />

                <ActionButtons
                    isLoading={isLoading}
                    fundEthBalance={fundEthBalance}
                    fundTokenBalance={fundTokenBalance}
                    walletTokenBalance={walletTokenBalance}
                    mode={mode}
                    tokenType={transactions.tokenType}
                    onDeposit={handleDeposit}
                    onWithdraw={handleWithdraw}
                    onMint={handleMint}
                    onWithdrawTokens={handleWithdrawTokens}
                    onRedeemFund={handleRedeemFund}
                    onRedeemWallet={handleRedeemWallet}
                    onDepositTokens={handleDepositTokens}
                    onShowTokenRedemption={toggleTokenRedemption}
                    showingTokenRedemption={showTokenRedemption}
                    inputAmount={inputAmount}
                    onRecoverTokens={handleRecoverTokens}
                />
                
                {/* Token Redemption Panel - only shown when mode is 'mint' and showTokenRedemption is true */}
                {mode === 'mint' && showTokenRedemption && (
                  <div className="mt-6">
                    <TokenRedemptionPanel
                      contract={contract}
                      fundTokenBalance={fundTokenBalance}
                      isLoading={isLoading}
                      setError={setDataError}
                      setSuccess={() => {}} // You may want to implement success messaging
                      onRedeemToken={handleTokenRedemption}
                    />
                  </div>
                )}
              </div>
              
              {/* About Fund Section */}
              <AboutFund />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default App;